import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, MapPin, Clock, Users, CreditCard, ArrowRight, 
  ArrowLeft, Loader2, Share2,
  AlertCircle, Tag, Home, ChevronRight
} from 'lucide-react';
import { getEvent, fetchAllEventsPages, DEFAULT_BASE_URL } from '../services/api';
import { getCurrentLanguage } from '../services/dataSync';
import DOMPurify from 'dompurify';
import { getEventStartEnd, isEventExpired as isEventExpiredUtil, getEventRegistrationState } from '../utils/eventDateTime';

const getApiOrigin = () => {
  try {
    const parsed = new URL(DEFAULT_BASE_URL.replace(/\/$/, ''));
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getEventImageSrc = (event) => {
  const raw = event?.imageUrl || event?.image || '';
  if (!raw) return '';
  if (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (raw.startsWith('/')) return `${getApiOrigin()}${raw}`;
  return raw;
};

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState({});
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [found, all] = await Promise.all([
          getEvent(id),
          fetchAllEventsPages()
        ]);
        setItem(found || null);
        
        // Get 3 recent events max to avoid overcrowding
        const recentEvents = (all || [])
          .filter(e => (e._id || e.id) !== id && e.isPublished)
          .slice(0, 3);
        setRecent(recentEvents);
      } catch (error) {
        console.error('Error loading event:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const isEventExpired = () => {
    if (!item) return false;
    return isEventExpiredUtil(item);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return String(dateValue);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const trimmed = String(timeString).trim();
    if (!trimmed) return '';
    if (/[AaPp][Mm]$/.test(trimmed)) return trimmed;
    const date = new Date(`2000-01-01T${trimmed}`);
    if (Number.isNaN(date.getTime())) return trimmed;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: item?.title,
          text: item?.description?.substring(0, 100),
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const getEventStatus = (event) => {
    if (!event) return '';
    if (event.status === 'Cancelled') return 'Cancelled';
    if (event.status === 'Completed') return 'Completed';
    if (event.status === 'Ongoing') return 'Ongoing';
    if (event.status === 'Upcoming') return 'Upcoming';

    const now = new Date();
    const { start, end } = getEventStartEnd(event);
    if (!start || !end) return 'Upcoming';

    if (now < start) return 'Upcoming';
    if (now >= start && now <= end) return 'Ongoing';
    return 'Completed';
  };

  const getStatusColor = (status) => {
    if (status === 'Cancelled') return 'bg-red-100 text-red-800';
    if (status === 'Upcoming') return 'bg-green-100 text-green-800';
    if (status === 'Ongoing') return 'bg-yellow-100 text-yellow-800';
    if (status === 'Completed') return 'bg-gray-100 text-gray-800';
    return 'bg-blue-100 text-blue-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center py-16">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto text-center py-12 bg-white rounded-lg shadow">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Event Not Found</h2>
            <p className="text-gray-600 mb-4">The event you're looking for doesn't exist.</p>
            <Link 
              to="/events" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Home className="h-4 w-4" />
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const eventDate = item.endDate || item.startDate || item.date;
  const startTime = item.startTime || item.time;
  const endTime = item.endTime;
  const registrationFee = item.fees?.amount || 0;
  const imageSrc = getEventImageSrc(item);
  const { state: registrationState, start: registrationStart, end: registrationEnd } = getEventRegistrationState(item);
  const hasRegistrationWindow = Boolean(registrationStart || registrationEnd);
  const canRegisterNow =
    item.isPublished !== false &&
    item.registrationRequired &&
    item.status !== 'Cancelled' &&
    registrationState === 'open' &&
    !(item.maxParticipants && item.currentParticipants >= item.maxParticipants) &&
    (hasRegistrationWindow || (item.status !== 'Completed' && !isEventExpired()));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Simple Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link 
            to="/events" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </Link>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <Share2 className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Main Grid - Simple 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Section */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {(imageSrc && !imageError[id]) ? (
                <div className="aspect-video w-full bg-gray-100">
                  <img 
                    src={imageSrc}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(prev => ({ ...prev, [id]: true }))}
                  />
                </div>
              ) : (
                <div className="aspect-video w-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                  <span className="text-5xl font-bold text-white opacity-50">
                    {item.title?.charAt(0) || 'E'}
                  </span>
                </div>
              )}

              {/* Content */}
              <div className="p-6">
                {/* Title and Status */}
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <h1 className="text-2xl font-bold text-gray-900">{item.title}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(getEventStatus(item))}`}>
                    {getEventStatus(item)}
                  </span>
                </div>

                {/* Event Meta - Simple List */}
                <div className="space-y-3 mb-6">
                  {eventDate && (
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">{formatDate(eventDate)}</p>
                        {(startTime || endTime) && (
                          <p className="text-sm text-gray-600">
                            {formatTime(startTime)}{endTime && ` - ${formatTime(endTime)}`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {item.location && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-900">{item.location}</p>
                    </div>
                  )}
                </div>

                {/* Category */}
                {item.category && (
                  <div className="mb-6">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                      <Tag className="h-3 w-3" />
                      {item.category}
                    </span>
                  </div>
                )}

                {/* Description */}
                {item.description && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">About</h2>
                    <div 
                      className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description) }} 
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Registration Card */}
            {item.registrationRequired && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Registration</h2>
                
                {/* Price */}
                <div className="mb-4 pb-4 border-b">
                  <p className="text-sm text-gray-600 mb-1">Fee</p>
                  {registrationFee > 0 ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-blue-600">
                        ₹{new Intl.NumberFormat('en-IN', {
                          minimumFractionDigits: Number.isInteger(registrationFee) ? 0 : 2,
                          maximumFractionDigits: 2
                        }).format(Number(registrationFee))}
                      </span>
                    </div>
                  ) : (
                    <span className="text-2xl font-bold text-green-600">FREE</span>
                  )}
                </div>

                {(registrationStart || registrationEnd) && (
                  <div className="mb-4 pb-4 border-b">
                    <p className="text-sm text-gray-600 mb-1">Registration Window</p>
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                      <p className="text-sm text-gray-900">
                        {registrationStart && registrationEnd
                          ? `${formatDateTime(registrationStart)} - ${formatDateTime(registrationEnd)}`
                          : registrationStart
                            ? `Opens: ${formatDateTime(registrationStart)}`
                            : `Closes: ${formatDateTime(registrationEnd)}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Availability */}
                {item.maxParticipants && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Available Spots</span>
                      <span className="font-medium">
                        {Math.max(0, item.maxParticipants - (item.currentParticipants || 0))} / {item.maxParticipants}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${((item.currentParticipants || 0) / item.maxParticipants) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Status Messages */}
                {item.status === 'Cancelled' ? (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">Event Cancelled</p>
                  </div>
                ) : !item.isPublished ? (
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-700">Coming Soon</p>
                  </div>
                ) : registrationState === 'misconfigured' ? (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">Registration Unavailable</p>
                    <p className="text-xs text-red-600 mt-1">Please contact support.</p>
                  </div>
                ) : registrationState === 'not_open' && registrationStart ? (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">Registration Opens Soon</p>
                    <p className="text-xs text-blue-600 mt-1">{formatDateTime(registrationStart)}</p>
                  </div>
                ) : registrationState === 'closed' && registrationEnd ? (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">Registration Closed</p>
                    <p className="text-xs text-red-600 mt-1">{formatDateTime(registrationEnd)}</p>
                  </div>
                ) : !hasRegistrationWindow && item.status === 'Completed' ? (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">Registration Closed</p>
                  </div>
                ) : !hasRegistrationWindow && isEventExpired() ? (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">Registration Closed</p>
                  </div>
                ) : item.maxParticipants && item.currentParticipants >= item.maxParticipants ? (
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-700">Event Full</p>
                  </div>
                ) : canRegisterNow ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate(`/event-registration/${id}`)}
                      className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <CreditCard className="h-4 w-4" />
                      Register Now
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">Registration Closed</p>
                  </div>
                )}

              </div>
            )}

            {/* Event Info Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Details</h2>
              <div className="space-y-2 text-sm">
                {item.organizer && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Organizer</span>
                    <span className="font-medium text-gray-900">{item.organizer}</span>
                  </div>
                )}
                {item.contactEmail && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Contact</span>
                    <a href={`mailto:${item.contactEmail}`} className="text-blue-600 hover:underline">
                      {item.contactEmail}
                    </a>
                  </div>
                )}
                {item.maxParticipants && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Capacity</span>
                    <span className="font-medium text-gray-900">{item.maxParticipants}</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Registration</span>
                  <span className="font-medium text-gray-900">
                    {item.registrationRequired ? 'Required' : 'Optional'}
                  </span>
                </div>
              </div>
            </div>

            {/* Recent Events - Simple Cards */}
            {recent.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">More Events</h2>
                  <Link to="/events" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    View all
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                
                <div className="space-y-3">
                  {recent.map((event) => {
                    const status = getEventStatus(event);
                    const eventImage = getEventImageSrc(event);
                    const eventDate = event.startDate || event.date;
                    
                    return (
                      <Link
                        key={event._id || event.id}
                        to={`/events/${event._id || event.id}`}
                        className="block p-3 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className="flex gap-3">
                          {/* Thumbnail */}
                          <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                            {eventImage && !imageError[event._id || event.id] ? (
                              <img 
                                src={eventImage}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={() => setImageError(prev => ({ ...prev, [event._id || event.id]: true }))}
                              />
                            ) : (
                              <div className="w-full h-full bg-blue-100 flex items-center justify-center">
                                <Calendar className="h-5 w-5 text-blue-600" />
                              </div>
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {event.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              {eventDate && (
                                <span className="text-xs text-gray-500">
                                  {formatDate(eventDate)}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(status)}`}>
                                {status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
